import {FC, useEffect, useRef, useState} from 'react'
import clsx from 'clsx'
import moment from 'moment'
import {useIntl} from 'react-intl'
import {updateQuery} from '../slices/csrFilterSlice'
import {DateRangePicker} from 'react-bootstrap-daterangepicker'
import {useAppDispatch, useAppSelector} from '../../../hooks/redux'
import {CSRGroupBy} from '../core/_models'

type Props = {
  language?: 'en' | 'ru'
  timeZone?: string
  rangeId: string
  onlyIcon: boolean
}

moment.updateLocale('default', {week: {dow: 1}})

export const DateRangePickerInput: FC<Props> = ({language, timeZone, rangeId, onlyIcon}) => {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const pickerRef = useRef<any>(null)
  const query = useAppSelector((state) => state.csrFilter)

  useEffect(() => {
    if (query?.period) {
      const [start, end] = query.period.split(',')
      handleCallback(moment.utc(start), moment.utc(end))
    } else {
      handleCallback(moment.utc(), moment.utc())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (query.group_by === 'day') {
      handleCallback(moment().startOf('week').startOf('day'), moment().endOf('day'))
    }

    if (query.group_by === 'week') {
      handleCallback(moment().subtract(29, 'days').startOf('day'), moment().endOf('day'))
    }

    if (query.group_by === 'month') {
      handleCallback(moment().subtract(1, 'year').startOf('day'), moment().endOf('day'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.group_by])

  const [state, setState] = useState<{
    start: moment.Moment
    end: moment.Moment
  }>({
    start: moment().startOf('day'),
    end: moment().endOf('day'),
  })

  const localeSettings = {
    format: 'DD.MM.YYYY',
    separator: ' - ',
    applyLabel: intl.formatMessage({id: 'FILTERS.BUTTON.APPLY'}),
    cancelLabel: intl.formatMessage({id: 'COMMON.BUTTON.CLOSE'}),
    timeZone: timeZone,
    customRangeLabel: intl.formatMessage({id: 'DATERANGEPICKER.CUSTOM'}),
    daysOfWeek: [
      intl.formatMessage({id: 'DATERANGEPICKER.DAYS.SU'}),
      intl.formatMessage({id: 'DATERANGEPICKER.DAYS.MO'}),
      intl.formatMessage({id: 'DATERANGEPICKER.DAYS.TU'}),
      intl.formatMessage({id: 'DATERANGEPICKER.DAYS.WE'}),
      intl.formatMessage({id: 'DATERANGEPICKER.DAYS.TH'}),
      intl.formatMessage({id: 'DATERANGEPICKER.DAYS.FR'}),
      intl.formatMessage({id: 'DATERANGEPICKER.DAYS.SA'}),
    ],
    monthNames: [
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.JENUARY'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.FEBRUARY'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.MARCH'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.APRIL'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.MAY'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.JUNE'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.JULY'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.AUGUST'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.SEPTEMBER'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.OCTOBER'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.NOVEMBER'}),
      intl.formatMessage({id: 'DATERANGEPICKER.MONTH.DECEMBER'}),
    ],
    firstDay: 1,
  }

  const rangeEngSettings = {
    'Current week': [moment().startOf('week').startOf('day').toDate(), moment().endOf('day').toDate()],
    'Last week': [
      moment().subtract(1, 'week').startOf('week').startOf('day').toDate(),
      moment().subtract(1, 'week').endOf('week').endOf('day').toDate(),
    ],
    'Last 30 days': [moment().subtract(29, 'days').startOf('day').toDate(), moment().endOf('day').toDate()],
    'Last month': [
      moment().subtract(1, 'month').startOf('month').startOf('day').toDate(),
      moment().subtract(1, 'month').endOf('month').endOf('day').toDate(),
    ],
    'Last 3 months': [moment().subtract(3, 'month').startOf('day').toDate(), moment().endOf('day').toDate()],
    'Last year': [moment().subtract(1, 'year').startOf('day').toDate(), moment().endOf('day').toDate()],
  }

  const rangeRusSettings = {
    'Текущая неделя': [moment().startOf('week').startOf('day').toDate(), moment().endOf('day').toDate()],
    'Прошлая неделя': [
      moment().subtract(1, 'week').startOf('week').startOf('day').toDate(),
      moment().subtract(1, 'week').endOf('week').endOf('day').toDate(),
    ],
    'Последние 30 дней': [moment().subtract(29, 'days').startOf('day').toDate(), moment().endOf('day').toDate()],
    'Прошлый месяц': [
      moment().subtract(1, 'month').startOf('month').startOf('day').toDate(),
      moment().subtract(1, 'month').endOf('month').endOf('day').toDate(),
    ],
    'Последние 3 месяца': [moment().subtract(3, 'month').startOf('day').toDate(), moment().endOf('day').toDate()],
    'Последний год': [moment().subtract(1, 'year').startOf('day').toDate(), moment().endOf('day').toDate()],
  }

  const getRangesByGroup = (groupBy: CSRGroupBy, ranges: any) => {
    if (groupBy === 'day') return ranges
    if (groupBy === 'week') {
      const {'Текущая неделя': _current, 'Прошлая неделя': _last, ...rest} = ranges
      return rest
    }
    if (groupBy === 'month') {
      return {
        'Последний год': ranges['Последний год'],
      }
    }
    return ranges
  }

  const handleCallback = (startUtc: moment.Moment, endUtc: moment.Moment) => {
    const tz = timeZone || moment.tz.guess()

    const startLocal = startUtc.clone().tz(tz).startOf('day')
    const endLocal = endUtc.clone().tz(tz).endOf('day')

    const startForApi = startLocal.clone().utc().format('YYYY-MM-DDTHH:mm:ss')
    const endForApi = endLocal.clone().utc().format('YYYY-MM-DDTHH:mm:ss')

    setState({start: startUtc, end: endUtc})
    dispatch(updateQuery({period: `${startForApi},${endForApi}`}))
  }

  useEffect(() => {
    if (pickerRef.current) {
      pickerRef.current.setStartDate(state.start.toDate())
      pickerRef.current.setEndDate(state.end.toDate())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  return (
    <DateRangePicker
      ref={pickerRef}
      key={`${language}-${query.group_by}`}
      initialSettings={{
        startDate: state.start.toDate(),
        endDate: state.end.toDate(),
        showDropdowns: true,
        timeZone: timeZone,
        ranges:
          language === 'ru'
            ? getRangesByGroup(query.group_by, rangeRusSettings)
            : getRangesByGroup(query.group_by, rangeEngSettings),
        locale: localeSettings,
        alwaysShowCalendars: false,
        showCustomRangeLabel: false,
        minYear: 2024,
        maxDate: moment().toDate(),
      }}
      onCallback={handleCallback}
    >
      <div className='input-group input-group-solid'>
        <span
          className={clsx('input-group-text', {
            'btn btn-small btn-light': onlyIcon === true,
          })}
        >
          <i className='fa fa-calendar p-0' />
        </span>
        {onlyIcon === false && (
          <input
            id={'reportRange' + rangeId}
            type='text'
            className='form-control form-control-lg form-control-solid text-gray-900 text-center test-sm cursor-pointer px-3'
            placeholder='Период'
            value={`${state.start
              .clone()
              .tz(timeZone || moment.tz.guess())
              .format('DD.MM.YYYY')} - ${state.end
              .clone()
              .tz(timeZone || moment.tz.guess())
              .format('DD.MM.YYYY')}`}
            readOnly
          />
        )}
      </div>
    </DateRangePicker>
  )
}
